import { useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, GraduationCap } from "lucide-react";
import { usePoll } from "@/lib/usePoll";
import { api, errMsg, signed, usd, ago } from "@/lib/api";
import { Panel, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";

export function DailySummary() {
  const { data, reload } = usePoll("/reports", 60000);
  const [busy, setBusy] = useState(false);
  const r = data?.[0];
  const gen = async () => {
    setBusy(true);
    try { await api.post("/reports/generate"); reload(); toast.success("Dagoverzicht bijgewerkt"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Panel title="Dagelijks overzicht · 21:00" icon={CalendarCheck} testid="daily-summary-panel"
      right={<Button size="sm" variant="secondary" onClick={gen} disabled={busy} data-testid="generate-summary-button" className="font-mono text-[10px] uppercase">Nu maken</Button>}>
      {!r ? <Empty>Het eerste overzicht verschijnt vanavond om 21:00.</Empty> : (
        <div className="font-mono text-xs space-y-1.5" data-testid="daily-summary-latest">
          <div className="text-zinc-500">{r.date} · {ago(r.ts)}</div>
          <div className="flex justify-between"><span className="text-zinc-400">Trades</span><span className="text-zinc-100">{r.trades} ({r.wins} winst)</span></div>
          <div className="flex justify-between"><span className="text-zinc-400">Netto winst</span><span className={r.net_pnl >= 0 ? "text-emerald-400" : "text-red-400"}>{signed(r.net_pnl, 2)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-400">Naar kluis vandaag</span><span className="text-blue-400">{usd(r.reserve_today, 2)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-400">Kluis totaal</span><span className="text-blue-400">{usd(r.reserve_total, 2)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-400">Actief / totaal</span><span className="text-zinc-100">{usd(r.active_capital, 2)} / {usd(r.total, 2)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-400">Flips</span><span className="text-[#F7931A]">{r.flips}/15</span></div>
          <div className="flex justify-between"><span className="text-zinc-400">Analyses · kansen</span><span className="text-zinc-300">{r.analyses} · {r.chances}</span></div>
        </div>)}
    </Panel>
  );
}

const NAMES = { market: "Market", whale: "Whale", orderbook: "Orderbook", derivatives: "Derivatives", technical: "Technical", news: "News/Macro", chief: "Chief" };

export function LearningPanel() {
  const { data } = usePoll("/learning", 60000);
  const rel = data?.reliability || {};
  return (
    <Panel title="Learning · gets better over time" icon={GraduationCap} testid="learning-panel">
      <p className="text-xs text-zinc-500 mb-3">Every analysis is replayed 12 hours later: would the risk engine's actual long/short plan have hit take-profit before stop-loss, after fees, slippage and funding? Agents are scored on that — not on raw direction. Scores with fewer than 30 samples are not reliable. Evaluated: {data?.evaluated_decisions ?? 0} · missed {data?.categories?.missed_opportunity ?? 0} · correctly avoided {data?.categories?.correctly_avoided ?? 0}.</p>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mb-4">
        {Object.keys(NAMES).map((k) => (
          <div key={k} className="flex justify-between font-mono text-xs border-b border-zinc-800/50 py-1" data-testid={`reliability-${k}`}>
            <span className="text-zinc-400">{NAMES[k]}</span>
            <span className={rel[k]?.reliable ? "text-zinc-200" : "text-zinc-500"}>{rel[k]?.hit_rate !== undefined && rel[k]?.hit_rate !== null ? `${Math.round(rel[k].hit_rate * 100)}% · n=${rel[k].evaluations}${rel[k].reliable ? "" : " (too few)"}` : "collecting…"}</span>
          </div>))}
      </div>
      <div className="font-mono text-[10px] text-zinc-500 mb-1">RECENT LESSONS</div>
      {!data?.lessons?.length ? <Empty>No lessons yet — they appear after missed moves and closed trades.</Empty> : (
        <div className="space-y-1.5 max-h-48 overflow-auto">{data.lessons.map((l) => <div key={l.id} className="text-xs text-zinc-400"><span className={l.type === "missed_move" ? "text-amber-400" : "text-emerald-400"}>▸</span> {l.text}</div>)}</div>)}
    </Panel>
  );
}
